import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import grapesjs from "grapesjs";
import "grapesjs/dist/css/grapes.min.css";
import "./grapes-template-editor.css";

const DEFAULT_HTML = `
  <div class="template-page">
    <div class="template-header">
      <h1>{{company.name}}</h1>
      <p>Document {{document.number}}</p>
    </div>
    <div class="template-body">
      <h2>Thank you, {{customer.name}}</h2>
      <p>Your document is ready to review.</p>
    </div>
    <div class="template-footer">{{company.email}}</div>
  </div>
`;

const DEFAULT_CSS = `
  .template-page { box-sizing: border-box; width: 800px; min-height: 1120px; padding: 48px; margin: 0 auto; background: #ffffff; color: #172033; font-family: Arial, sans-serif; }
  .template-header { border-bottom: 1px solid #dce2eb; padding-bottom: 24px; }
  .template-header h1 { margin: 0 0 8px; font-size: 28px; }
  .template-header p, .template-footer { color: #667085; }
  .template-body { padding: 56px 0; }
  .template-body h2 { font-size: 22px; }
  .template-footer { border-top: 1px solid #dce2eb; padding-top: 20px; font-size: 12px; }
  .template-variable { background: #fff4cc; border-radius: 3px; padding: 1px 4px; }
`;

const normaliseField = (field) => {
  const token = field?.fieldPlaceHolder || field?.placeholder || field?.fieldName;
  const label = field?.fieldName || field?.name || token;
  if (!token) return null;
  return { token, label };
};

const legacyElementsToHtml = (elements) =>
  elements
    .map((element) => {
      const styles = element?.styles || {};
      const styleText = Object.entries(styles)
        .map(([key, value]) => `${key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}:${value}`)
        .join(";");
      const content = element?.fieldPlaceHolder || element?.fieldName || "Text";
      const tag = element?.fieldPlaceHolder === "{TABLE}" ? "table" : "div";
      const tableBody =
        tag === "table"
          ? `<tbody><tr><td>{ITEM}</td><td>{QUANTITY}</td><td>{AMOUNT}</td></tr></tbody>`
          : "";
      return `<${tag} class="legacy-template-element" style="position:absolute;left:${Number(element?.x) || 0}px;top:${Number(element?.y) || 0}px;${styleText}">${content}${tableBody}</${tag}>`;
    })
    .join("");

const GrapesTemplateEditor = forwardRef(
  ({ availableFields = [], initialContent, desktopScreen = true, templateKind, onReady }, ref) => {
    const hostRef = useRef(null);
    const editorRef = useRef(null);
    const loadedInitialContent = useRef(false);

    useImperativeHandle(ref, () => ({
      getTemplateContent: () => {
        const editor = editorRef.current;
        if (!editor) return null;
        return {
          version: 1,
          editor: "grapesjs",
          projectData: editor.getProjectData(),
          html: editor.getHtml({ cleanId: true }),
          css: editor.getCss(),
          variables: Array.from(
            new Set(
              (editor.getHtml({ cleanId: true }).match(/{{[^{}]+}}/g) || []).map(
                (value) => value.slice(2, -2).trim(),
              ),
            ),
          ),
          templateKind,
        };
      },
    }));

    useEffect(() => {
      if (!hostRef.current || editorRef.current) return undefined;

      const editor = grapesjs.init({
        container: hostRef.current,
        height: "calc(100vh - 235px)",
        width: "auto",
        fromElement: false,
        storageManager: false,
        selectorManager: { componentFirst: true },
        deviceManager: {
          devices: [
            { id: "Desktop", name: "Desktop", width: "800px" },
            { id: "Mobile", name: "Mobile", width: "375px" },
          ],
        },
        canvas: { styles: ["body { margin: 0; background: #eef1f5; }"] },
        blockManager: { appendTo: "#template-builder-blocks" },
        panels: { defaults: [] },
      });

      editor.BlockManager.add("template-text", {
        label: "Text",
        category: "Basic",
        content: { type: "text", content: "Double-click to edit this text" },
      });
      editor.BlockManager.add("template-variable", {
        label: "Variable",
        category: "Basic",
        content: {
          type: "text",
          content: "{{field.name}}",
          attributes: { class: "template-variable" },
        },
      });
      editor.BlockManager.add("template-image", {
        label: "Image",
        category: "Basic",
        content: { type: "image", attributes: { src: "https://placehold.co/720x180?text=Image" } },
      });
      editor.BlockManager.add("template-button", {
        label: "Button",
        category: "Basic",
        content: '<a class="template-button" href="{{action.url}}">Open document</a>',
      });
      editor.BlockManager.add("template-divider", {
        label: "Divider",
        category: "Basic",
        content: "<hr>",
      });
      editor.BlockManager.add("template-table", {
        label: "Items table",
        category: "Documents",
        content: `<table class="template-table"><thead><tr><th>Item</th><th>Quantity</th><th>Amount</th></tr></thead><tbody><tr><td>{{item.name}}</td><td>{{item.quantity}}</td><td>{{item.amount}}</td></tr></tbody></table>`,
      });
      editor.BlockManager.add("template-page-break", {
        label: "Page break",
        category: "Documents",
        content: '<div class="template-page-break"></div>',
      });

      normaliseField({ fieldPlaceHolder: "{LINE}", fieldName: "Line" });
      availableFields.map(normaliseField).filter(Boolean).forEach(({ token, label }) => {
        editor.BlockManager.add(`template-field-${token}`, {
          label,
          category: "Available fields",
          content: {
            type: "text",
            content: token,
            attributes: { class: "template-variable", "data-template-variable": token },
          },
        });
      });

      editor.setComponents(DEFAULT_HTML);
      editor.setStyle(DEFAULT_CSS);
      editorRef.current = editor;
      onReady?.(true);

      return () => {
        editor.destroy();
        editorRef.current = null;
        onReady?.(false);
      };
    }, [availableFields, templateKind, onReady]);

    useEffect(() => {
      const editor = editorRef.current;
      if (!editor || loadedInitialContent.current || !initialContent) return;
      loadedInitialContent.current = true;
      try {
        const document = typeof initialContent === "string" ? JSON.parse(initialContent) : initialContent;
        if (document?.editor === "grapesjs" && document.projectData) {
          editor.loadProjectData(document.projectData);
        } else if (document?.html) {
          editor.setComponents(document.html);
          editor.setStyle(document.css || "");
        } else if (Array.isArray(document)) {
          editor.setComponents(`<div class="template-page legacy-template-page">${legacyElementsToHtml(document)}</div>`);
        }
      } catch (error) {
        // Keep the default canvas when a legacy or malformed template is encountered.
        console.warn("Unable to load template content", error);
      }
    }, [initialContent]);

    useEffect(() => {
      const editor = editorRef.current;
      if (!editor) return;
      editor.setDevice(desktopScreen ? "Desktop" : "Mobile");
    }, [desktopScreen]);

    return (
      <div className="legacy-grapes-template-editor">
        <div id="template-builder-blocks" className="template-builder-blocks" />
        <div ref={hostRef} className="template-builder-canvas" />
      </div>
    );
  },
);

export default GrapesTemplateEditor;
